import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from 'services/auth-service';
import AccountService, { AccountProps } from 'services/hooks/account-service';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import SelectInput from 'components/elements/Form/select/selectInput';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import IntegrationService from 'services/hooks/integration-service';
import { toast } from 'react-toastify';
import { generateApiFailErrorMessage } from 'utils/helpers';
import OrganizationService, { OrganizationProps } from 'services/hooks/organization-service';
import { defaultUUIDValue } from 'utils/constants';

export default function CSVRoleSelectionForm({ Controller, methods }: any) {
  const {
    state: { user }
  } = useAuth();
  const { trigger } = methods;

  const [buildingOptions, setBuildingOptions] = useState<{ value: string; label: string }[]>([]);
  const formatedOptions = (value: string, label: string) => {
    return { value: value, label: label };
  };

  const accountWatch = methods.watch('accountUuid');
  const [getAccountByIdResult, setGetAccountByIdResult] = useState<AccountProps | undefined>(
    undefined
  );

  // Fetch user specific Account data according to selected Account
  const { data: accountData } = useQuery<AccountProps, Error>(
    ['account', accountWatch],
    () => AccountService.findAccountById(accountWatch),
    {
      enabled: !!accountWatch && user.role === 'SUPER',
      onError: () => {
        toast.error(generateApiFailErrorMessage('Account'));
      }
    }
  );

  // Fetch user specific Account data
  const { data: userAccountData } = useQuery<AccountProps, Error>(
    'account',
    () => AccountService.findAccountById(user.uuid),
    {
      enabled: user.role === 'ACCOUNT',
      onError: () => {
        toast.error(generateApiFailErrorMessage('Account'));
      }
    }
  );

  // Fetch user specific Building data
  const { data: userBuildingData } = useQuery<BuildingProps, Error>(
    'building',
    () => BuildingService.findBuildingById(user.uuid),
    {
      enabled: user.role === 'BUILDING',
      onError: () => {
        toast.error(generateApiFailErrorMessage('Building'));
      }
    }
  );

  // Fetch user specific Organization data
  const { data: userOrgData } = useQuery<OrganizationProps, Error>(
    ['organization', user.uuid],
    () => OrganizationService.findOrgById(user.uuid),
    {
      enabled: user.role === 'ORG',
      onError: () => {
        toast.error(generateApiFailErrorMessage('Organization'));
      }
    }
  );

  // Fetch organization specific Building data
  const { data: orgBuildingData } = useQuery<BuildingProps, Error>(
    ['orgBuilding', userOrgData?.buildingUuid],
    () => BuildingService.findBuildingById(userOrgData?.buildingUuid),
    {
      enabled: user.role === 'ORG' && !!userOrgData?.buildingUuid,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Building'));
      }
    }
  );

  useEffect(() => {
    if (accountData && accountWatch) {
      setGetAccountByIdResult(accountData);
    }
  }, [accountData, accountWatch]);

  //UseEffect when the role is Super
  useEffect(() => {
    if (getAccountByIdResult && user.role === 'SUPER') {
      // Populate building options
      const options =
        getAccountByIdResult.buildings
          ?.filter((building): building is BuildingProps & { uuid: string } => !!building.uuid)
          .map((building) => formatedOptions(building.uuid, building.name)) || [];
      setBuildingOptions(options);
    }
  }, [getAccountByIdResult, user]);

  // UseEffect when the role is Account
  useEffect(() => {
    if (userAccountData && user.role === 'ACCOUNT') {
      // Preload account
      methods.setValue('accountUuid', userAccountData.uuid);

      // Populate building options
      const options =
        userAccountData.buildings
          ?.filter((building): building is BuildingProps & { uuid: string } => !!building.uuid)
          .map((building) => formatedOptions(building.uuid, building.name)) || [];
      setBuildingOptions(options);
    }
  }, [methods, user, userAccountData]);

  // UseEffect when the role is Building
  useEffect(() => {
    if (userBuildingData && user.role === 'BUILDING') {
      // Preload account and building
      methods.setValue('accountUuid', userBuildingData.accountUuid);
      methods.setValue('buildingUuid', userBuildingData.uuid);
    }
  }, [methods, user, userBuildingData]);

  // UseEffect when the role is Organization
  useEffect(() => {
    if (userOrgData && orgBuildingData && user.role === 'ORG') {
      // Preload account and building
      methods.setValue('accountUuid', orgBuildingData.accountUuid);
      methods.setValue('buildingUuid', userOrgData.buildingUuid);

      // Set integrationUuid if available and not default value
      if (userOrgData.integrationUuid && userOrgData.integrationUuid !== defaultUUIDValue) {
        methods.setValue('integrationUuid', userOrgData.integrationUuid);
        // Trigger re-validation when integrationUuid is set for Org role
        trigger();
      }
    }
  }, [methods, user, userOrgData, orgBuildingData, trigger]);

  const renderRoleBasedDetails = () => {
    switch (user.role) {
      case 'SUPER':
        return (
          <div className="grid grid-cols-2 gap-4 mr-8">
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Choose Integration*"
              placeholder="Select an Integration"
              queryname="integrations"
              name="integrationUuid"
              errors={methods.formState.errors}
              query={() => IntegrationService.findAllIntegrations()}
              expectedLabel="Integration/Name"
              multi={false}
            />
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Choose an Account*"
              placeholder="Select Account"
              queryname="accounts"
              name="accountUuid"
              errors={methods.formState.errors}
              query={AccountService.findAllAccounts}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose a Building*"
              placeholder="Select Building"
              name="buildingUuid"
              errors={methods.formState.errors}
              options={buildingOptions}
            />
          </div>
        );
      case 'ACCOUNT':
        return (
          <div className="grid grid-cols-2 gap-4 mr-8">
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Choose Integration*"
              placeholder="Select an Integration"
              queryname="integrations"
              name="integrationUuid"
              errors={methods.formState.errors}
              query={() => IntegrationService.findAllIntegrations()}
              expectedLabel="Integration/Name"
              multi={false}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose a Building*"
              placeholder="Select Building"
              name="buildingUuid"
              errors={methods.formState.errors}
              options={buildingOptions}
            />
          </div>
        );
      case 'BUILDING':
        return (
          <div className="grid grid-cols-2 gap-4 mr-8">
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Choose Integration*"
              placeholder="Select an Integration"
              queryname="integrations"
              name="integrationUuid"
              errors={methods.formState.errors}
              query={() => IntegrationService.findAllIntegrations()}
              expectedLabel="Integration/Name"
              multi={false}
            />
          </div>
        );
      case 'ORG':
        return (
          <div className="grid grid-cols-2 gap-4 mr-8">
            {(!userOrgData ||
              !userOrgData.integrationUuid ||
              userOrgData.integrationUuid === defaultUUIDValue) && (
              <SelectEntity
                Controller={Controller}
                control={methods.control}
                label="Choose Integration*"
                placeholder="Select an Integration"
                queryname="integrations"
                name="integrationUuid"
                errors={methods.formState.errors}
                query={() => IntegrationService.findAllIntegrations()}
                expectedLabel="Integration/Name"
                multi={false}
              />
            )}
          </div>
        );
      default:
        console.log('Role is missing');
        return null;
    }
  };

  return renderRoleBasedDetails();
}
